/**
 * Shape-matched loading skeleton for dashboard pages. Every page's
 * first-paint uses the same skeleton so the transition to loaded content
 * feels instant — the metrics/chart boxes are already there, just filling in.
 *
 * Designed to match the common layout: 4 metric cards + chart + table.
 * Pages with different layouts (Sales' 3-column briefing area) still look
 * reasonable under this skeleton — the goal is motion cue, not pixel match.
 */
export function DashboardSkeleton() {
  return (
    <div className="space-y-5 max-w-[1400px] animate-pulse">
      {/* Metric row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="bg-surface border border-border-default rounded-card px-5 py-4 h-[100px]"
          >
            <div className="h-2 w-16 bg-border-default rounded mb-3" />
            <div className="h-7 w-20 bg-border-default rounded mb-2" />
            <div className="h-2 w-24 bg-border-light rounded" />
          </div>
        ))}
      </div>

      {/* Chart */}
      <div className="bg-surface border border-border-default rounded-card">
        <div className="px-5 py-4 border-b border-border-light">
          <div className="h-3 w-32 bg-border-default rounded mb-2" />
          <div className="h-2 w-48 bg-border-light rounded" />
        </div>
        <div className="px-5 py-4">
          <div className="h-[220px] bg-border-light rounded" />
        </div>
      </div>

      {/* Two-column row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-surface border border-border-default rounded-card">
          <div className="px-5 py-4 border-b border-border-light">
            <div className="h-3 w-36 bg-border-default rounded" />
          </div>
          <div className="p-4 space-y-2.5">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="h-5 bg-border-light rounded" />
            ))}
          </div>
        </div>
        <div className="bg-surface border border-border-default rounded-card">
          <div className="px-5 py-4 border-b border-border-light">
            <div className="h-3 w-24 bg-border-default rounded" />
          </div>
          <div className="p-4 space-y-3">
            {[0, 1].map((i) => (
              <div key={i}>
                <div className="flex justify-between mb-1">
                  <div className="h-2 w-16 bg-border-light rounded" />
                  <div className="h-2 w-12 bg-border-light rounded" />
                </div>
                <div className="h-1.5 bg-border-light rounded-full" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
